const REMEMBER_MAX_AGE = 60 * 60 * 24 * 30

export const useAuthToken = () => {
  const config = useRuntimeConfig()

  // Cookie domain rules:
  //   - Real root domain (zerotohero.network) → ".zerotohero.network" so
  //     one login is shared across tenant subdomains.
  //   - Local dev (.localhost) → leave UNDEFINED. Chrome and Firefox both
  //     reject explicit cookies with `domain=.localhost`, so the cookie
  //     was silently being dropped on login and subsequent requests had
  //     no Authorization header (manifested as 401 on PATCH /auth/profile
  //     and GET /membership/me right after a successful POST /auth/login).
  //     Each *.localhost subdomain gets its own session in dev — fine.
  const cookieDomain = import.meta.client ? (() => {
    const root = String(config.public.platformRootDomain || '').toLowerCase().trim()
    if (!root || root === 'localhost') return undefined
    const host = window.location.hostname
    return (host === root || host.endsWith('.' + root)) ? '.' + root : undefined
  })() : undefined

  const token = useCookie<string | null>('auth-token', {
    sameSite: 'lax',
    secure: import.meta.env.PROD,
    domain: cookieDomain
  })

  const setToken = (value: string | null | undefined, remember?: boolean) => {
    const trimmed = value?.trim() || null

    if (trimmed && remember) {
      const persistent = useCookie<string | null>('auth-token', {
        sameSite: 'lax',
        secure: import.meta.env.PROD,
        domain: cookieDomain,
        maxAge: REMEMBER_MAX_AGE
      })
      persistent.value = trimmed
      return
    }

    token.value = trimmed
  }

  const clearToken = () => {
    token.value = null
  }

  return {
    token,
    setToken,
    clearToken
  }
}
